import {
  BrowserAuthError,
  InteractionRequiredAuthError,
  InteractionStatus,
} from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useEffect } from 'react';
import { apiClient } from '../api/client';
import { apiRequest, loginRequest } from './authConfig';
import { clearMsalState } from './msalInstance';

/**
 * Custom hook for authentication logic
 * Handles login, logout, and API token setup
 */
export const useAuth = () => {
  const { instance, accounts, inProgress } = useMsal();

  // Set up API client with access token provider
  useEffect(() => {
    if (accounts.length > 0) {
      apiClient.setAccessTokenProvider(async () => {
        try {
          const response = await instance.acquireTokenSilent({
            ...apiRequest,
            account: accounts[0],
          });
          return response.accessToken;
        } catch (error) {
          console.error('Failed to acquire access token:', error);

          // If interaction is required or any auth error occurs, clear state and redirect to login
          if (error instanceof InteractionRequiredAuthError || error instanceof BrowserAuthError) {
            console.warn('Authentication failed. Clearing auth state and redirecting to login...');
            clearMsalState();
            window.location.href = '/';
            throw error; // Re-throw to prevent API call
          }

          throw error;
        }
      });
    }
  }, [instance, accounts]);

  const handleLogin = async () => {
    // Don't try to login if already authenticated
    if (accounts.length > 0) {
      return;
    }

    // Prevent login if an interaction is already in progress
    if (inProgress !== InteractionStatus.None) {
      console.warn(
        'Login interaction already in progress. If stuck, use "Clear Login State" button.',
      );
      return;
    }

    try {
      await instance.loginRedirect(loginRequest);
    } catch (e) {
      if (e instanceof BrowserAuthError && e.errorCode === 'interaction_in_progress') {
        // Automatically clear state if interaction is stuck
        console.error('Login blocked by stale interaction. Clearing state...');
        handleClearLoginState();
      } else {
        console.error('Login error:', e);
      }
    }
  };

  const handleLogout = () => {
    // Logout only from the app, not from the Azure AD account
    // This clears the local session without invalidating the Azure AD session
    // Users will remain logged into their Microsoft account and other apps
    clearMsalState();
    window.location.href = '/';
  };

  const handleClearLoginState = () => {
    clearMsalState();
    window.location.reload();
  };

  return {
    accounts,
    inProgress,
    handleLogin,
    handleLogout,
    handleClearLoginState,
  };
};
